import { useState, useEffect } from "react";
import Dashboard from "./components/Dashboard/Dashboard";
import { Routes, Route, useLocation } from "react-router-dom";
import Register from "./components/User/Register";
import Login from "./components/User/LogIn";
import Settings from "./components/Settings/Settings";
import EmailStats from "./components/Dashboard/EmailStats";
import ForgotPassword from "./components/User/ForgotPassword";
import VerifyOtp from "./components/User/VerifyOtp";
import ResetPassword from "./components/User/ResetPassword";
import Accounts from "./components/Dashboard/Accounts";
import Calendar from "./components/Calendar/Calendar";
import EmailSection from "./components/Email/EmailSection";
import ReplyEmail from "./components/Dashboard/ReplyEmail";
import UserConfigModal from "./components/Dashboard/UserConfigModal";
import VerificationCode from "./components/User/VerificationCode";
import ChatBotWrapper from "./components/ChatBot/ChatBotWrapper";
import CommingSoon from "./components/CommingSoon/CommingSoon";

// Global error handler setup
const setupGlobalErrorHandling = () => {
  // Store original console.error
  const originalConsoleError = console.error;

  // Override console.error to provide more details
  console.error = function (...args) {
    // Call original console.error
    originalConsoleError.apply(console, args);

    // If this is an error object, log additional details
    if (args[0] instanceof Error) {
      const error = args[0];
      originalConsoleError("DETAILED ERROR INFO:");
      originalConsoleError("Name:", error.name);
      originalConsoleError("Message:", error.message);
      originalConsoleError("Stack:", error.stack);

      // Try to parse the stack to find the source
      if (error.stack) {
        const stackLines = error.stack.split("\n");
        originalConsoleError("Stack Lines:", stackLines.length);
        stackLines.forEach((line, index) => {
          originalConsoleError(`Stack[${index}]:`, line);
        });
      }
    }
  };

  // Monitor all global errors
  window.addEventListener("error", (event) => {
    console.group("GLOBAL ERROR CAUGHT:");
    console.error("Error Object:", event.error);
    console.error("Error Type:", event.error?.constructor?.name);
    console.error("Message:", event.message);
    console.error("Filename:", event.filename);
    console.error("Line Number:", event.lineno);
    console.error("Column Number:", event.colno);

    // Try to identify what 's' is in the error context
    try {
      // This specifically targets the error we're seeing
      if (event.error?.message?.includes("s is not a function")) {
        console.error('CRITICAL: Found "s is not a function" error!');
        console.error(
          "Current React Components:",
          Array.from(
            document.querySelectorAll("[data-reactroot], [data-reactid]")
          ).map((el) => ({
            component: el.tagName,
            id: el.id,
            className: el.className,
          }))
        );
      }
    } catch (e) {
      console.error("Error while analyzing error context:", e);
    }

    console.groupEnd();
  });
};

// Debug wrapper component to catch errors in any component
function DebugErrorBoundary({ children, componentName }) {
  const [hasError, setHasError] = useState(false);
  const [error, setError] = useState(null);
  const [errorInfo, setErrorInfo] = useState(null);

  useEffect(() => {
    // Create error handler
    const errorHandler = (event) => {
      console.group(`ERROR IN ${componentName}:`);
      console.error("Error:", event.error);
      console.error("Error Message:", event.error?.message);
      console.error("Error Stack:", event.error?.stack);
      console.error("Source:", event.filename);
      console.error("Line:", event.lineno);
      console.error("Column:", event.colno);
      console.groupEnd();

      // Store detailed error info
      setError(event.error);
      setErrorInfo({
        source: event.filename,
        line: event.lineno,
        column: event.colno,
      });
      setHasError(true);

      // Prevent default error handling
      event.preventDefault();
    };

    // Add error handler
    window.addEventListener("error", errorHandler);

    // Add unhandled promise rejection handler
    const promiseErrorHandler = (event) => {
      console.group(`UNHANDLED PROMISE REJECTION IN ${componentName}:`);
      console.error("Error:", event.reason);
      console.error("Stack:", event.reason?.stack);
      console.groupEnd();

      setError(event.reason);
      setHasError(true);
      event.preventDefault();
    };

    window.addEventListener("unhandledrejection", promiseErrorHandler);

    // Clean up
    return () => {
      window.removeEventListener("error", errorHandler);
      window.removeEventListener("unhandledrejection", promiseErrorHandler);
    };
  }, [componentName]);

  if (hasError) {
    return (
      <div
        style={{
          margin: "20px",
          padding: "20px",
          border: "1px solid #f5c6cb",
          borderRadius: "5px",
          backgroundColor: "#f8d7da",
          color: "#721c24",
        }}
      >
        <h2>Something went wrong in {componentName}</h2>
        <p>Error: {error?.message || "Unknown error"}</p>
        {errorInfo && (
          <details style={{ whiteSpace: "pre-wrap", marginTop: "10px" }}>
            <summary>Error Details</summary>
            <p>Source: {errorInfo.source}</p>
            <p>Line: {errorInfo.line}</p>
            <p>Column: {errorInfo.column}</p>
            <p>Stack Trace:</p>
            <pre
              style={{
                backgroundColor: "#f8f9fa",
                padding: "10px",
                borderRadius: "4px",
                overflow: "auto",
              }}
            >
              {error?.stack}
            </pre>
          </details>
        )}
        <button
          onClick={() => (window.location.href = "/")}
          style={{
            padding: "8px 16px",
            backgroundColor: "#0066cc",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            marginTop: "10px",
          }}
        >
          Go to Home
        </button>
      </div>
    );
  }

  return children;
}

// Debug component to log route changes
function RouteDebugger() {
  const location = useLocation();

  useEffect(() => {
    console.log("%c[Route Change]", "color: #4CAF50; font-weight: bold", {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
      state: location.state,
      timestamp: new Date().toISOString(),
    });

    // Check for tokens in URL and log them
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.has("access_token")) {
      console.log("Found token in URL params:", {
        access_token: searchParams.get("access_token").substring(0, 10) + "...",
        user_id: searchParams.get("user_id"),
        account_id: searchParams.get("account_id"),
      });
    }
  }, [location]);

  return null;
}

function App() {
  useEffect(() => {
    // Set up global error handling
    setupGlobalErrorHandling();

    console.log("%c[App Debug]", "color: #9C27B0; font-weight: bold", {
      app_render_time: new Date().toISOString(),
      window_location: window.location.href,
      user_agent: navigator.userAgent,
    });
  }, []);

  return (
    <>
      <RouteDebugger />
      <Routes>
        <Route
          path="/dashboard"
          element={
            <DebugErrorBoundary componentName="Dashboard">
              <Dashboard />
            </DebugErrorBoundary>
          }
        />
        <Route
          path="/dashboard/email/:emailId"
          element={
            <DebugErrorBoundary componentName="Dashboard with Email">
              <Dashboard />
            </DebugErrorBoundary>
          }
        />
        <Route
          path="/email"
          element={
            <DebugErrorBoundary componentName="EmailSection">
              <EmailSection />
            </DebugErrorBoundary>
          }
        />
        <Route
          path="/email/reply/:emailId"
          element={
            <DebugErrorBoundary componentName="EmailSection Reply">
              <EmailSection />
            </DebugErrorBoundary>
          }
        />
        <Route
          path="/calendar"
          element={
            <DebugErrorBoundary componentName="Calendar">
              <Calendar />
            </DebugErrorBoundary>
          }
        />
        <Route
          path="/settings"
          element={
            <DebugErrorBoundary componentName="Settings">
              <Settings />
            </DebugErrorBoundary>
          }
        />
        <Route
          path="/analytics"
          element={
            <DebugErrorBoundary componentName="UserConfigModal">
              <CommingSoon />
            </DebugErrorBoundary>
          }
        />
        <Route
          path="/register"
          element={
            <DebugErrorBoundary componentName="Register">
              <Register />
            </DebugErrorBoundary>
          }
        />
        <Route
          path="/email-verification"
          element={
            <DebugErrorBoundary componentName="Login">
              <VerificationCode />
            </DebugErrorBoundary>
          }
        />
        <Route
          path="/"
          element={
            <DebugErrorBoundary componentName="Login">
              <Login />
            </DebugErrorBoundary>
          }
        />
        <Route
          path="/email-stats"
          element={
            <DebugErrorBoundary componentName="EmailStats">
              <EmailStats />
            </DebugErrorBoundary>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <DebugErrorBoundary componentName="ForgotPassword">
              <ForgotPassword />
            </DebugErrorBoundary>
          }
        />
        <Route
          path="/verify"
          element={
            <DebugErrorBoundary componentName="VerifyOtp">
              <VerifyOtp />
            </DebugErrorBoundary>
          }
        />
        <Route
          path="/reset"
          element={
            <DebugErrorBoundary componentName="ResetPassword">
              <ResetPassword />
            </DebugErrorBoundary>
          }
        />
        <Route
          path="/accounts"
          element={
            <DebugErrorBoundary componentName="Accounts">
              <Accounts />
            </DebugErrorBoundary>
          }
        />
        <Route
          path="/reply/${email.email.id}"
          element={
            <DebugErrorBoundary componentName="ReplyEmail">
              <ReplyEmail />
            </DebugErrorBoundary>
          }
        />
        <Route
          path="/user-config"
          element={
            <DebugErrorBoundary componentName="UserConfigModal">
              <UserConfigModal />
            </DebugErrorBoundary>
          }
        />
      </Routes>
      {/* ChatBot component will appear on all pages except login and registration pages */}
      <ChatBotWrapper />
    </>
  );
}

export default App;
